/**
 * 화이트보드 코어 API
 * v1 원본: utils/whiteboard/index.js (630줄)
 * v2 변환: TypeScript, Redux Toolkit
 *
 * 핵심 흐름:
 * - 세션 시작: setMetadata → METADATA_UPDATED 이벤트 → 모든 참여자가 화이트보드 열기
 * - 세션 종료: removeMetadata → METADATA_UPDATED → 모든 참여자가 화이트보드 닫기
 * - 문서 공유: Object Storage 업로드 → Office Preview → 페이지별 이미지 변환 → Excalidraw collaboration room 생성
 */
import axios from 'axios';
import { store } from '@/store';
import { whiteboardActions } from '@/store/slices/whiteboardSlice';
import { participantsActions } from '@/store/slices/participantsSlice';
import { mainUserActions } from '@/store/slices/mainUserSlice';
import { uiActions } from '@/store/slices/uiSlice';
import type { CollabDetails } from '@/store/slices/whiteboardSlice';
import { WHITEBOARD_ID, METADATA_KEYS, COLLAB_SERVER_URL } from './constants';
import { generateCollaborationLinkData } from '@jitsi/excalidraw';
import { convertUrlToBase64, appendURLHashParam } from './baseUtils';
import { uploadFiles, previewFilesUnauthorized } from '@/utils/objectStorage';

/**
 * MetadataHandler 접근자
 * lib-jitsi-meet의 room.getMetadataHandler() 사용
 */
function getMetadataHandler(): {
  setMetadata: (key: string, data: unknown) => void;
  getMetadata: () => Record<string, unknown>;
} | null {
  const room = (window as any)._jitsiRoom;
  if (!room) return null;
  return room.getMetadataHandler?.() ?? null;
}

function getRoom() {
  return (window as any)._jitsiRoom ?? null;
}

// ─── 세션 시작/종료 ────────────────────────────────────────────────────────

/**
 * 스케치 모드 세션 시작
 * v1 흐름: generateCollaborationLinkData → setMetadata(3개) → dispatch
 */
export async function startWhiteboardSession(isAllEditable = false): Promise<void> {
  const room = getRoom();
  if (!room) return;

  const metadataHandler = getMetadataHandler();
  if (!metadataHandler) return;

  const collabDetails = await generateCollaborationLinkData();
  const ownerId = room.myUserId();

  // broadcast-batch: collab 서버에 방 정보 및 권한 정보 등록
  await axios({
    headers: { 'Content-Type': 'application/json' },
    method: 'POST',
    url: `${COLLAB_SERVER_URL}/api/broadcast-batch`,
    data: {
      roomList: [{
        roomID: collabDetails.roomId,
        roomKey: collabDetails.roomKey,
        rawData: [],
      }],
      pdfFileName: null,
      conferenceId: room.getName?.() ?? room.options?.name ?? '',
      ownerJitsiId: ownerId,
      editPermissions: isAllEditable ? null : [ownerId],
    },
  });

  // 메타데이터 설정 → METADATA_UPDATED 이벤트 발생 → 모든 참여자에게 전달
  metadataHandler.setMetadata(METADATA_KEYS.WHITEBOARD_OWNER, ownerId);
  metadataHandler.setMetadata(METADATA_KEYS.WHITEBOARD_ID, collabDetails);
  metadataHandler.setMetadata(METADATA_KEYS.FILE_METADATA, { files: null });

  // 로컬 상태 업데이트 (원격은 METADATA_UPDATED 핸들러에서 처리)
  const { local } = store.getState();
  const ownerName = local.userName || local.nickname || '나';
  store.dispatch(participantsActions.addPseudoParticipant({
    id: WHITEBOARD_ID,
    name: `${ownerName}의 스케치 공유`,
    isWhiteboard: true,
    ownerId,
  }));
  store.dispatch(whiteboardActions.openWhiteboard({
    ownerId,
    isSketchMode: true,
    collabDetails,
    isOwner: true,
  }));
  // 스케치 모드 시작 시 사이드 패널(채팅/참석자 등)이 열려있으면 닫기 — 코치마크 위치 보정
  store.dispatch(uiActions.setOpenSideBar(false));
  // 화이트보드를 메인 유저로 핀 고정 + 사이드뷰로 전환
  store.dispatch(mainUserActions.pinUser(WHITEBOARD_ID));
  store.dispatch(uiActions.setScreenMode('SIDE'));

  // ─── 모바일 테스트용: 새 탭으로 WhiteboardPage 열기 ────────────────────
  const { local: localState } = store.getState();
  const whiteboardUrl = appendURLHashParam(
    window.location.origin + '/whiteboard',
    'state',
    JSON.stringify({
      roomId: collabDetails.roomId,
      roomKey: collabDetails.roomKey,
      localParticipantName: localState.userName || localState.nickname || '익명',
      jitsiId: ownerId,
      initialZoom: 0.5,
      conferenceId: room.getName?.() ?? room.options?.name ?? '',
    }),
  );

  console.log('\n========== [모바일테스트용] 스케치 모드 ==========');
  console.log('■ WhiteboardPage URL (네이티브에서 WebView로 열어야 할 URL):');
  console.log(whiteboardUrl);
  console.log('\n■ 네이티브 → WebView postMessage: 없음 (스케치 모드에서는 FILE_LIST 불필요)');
  console.log('=================================================\n');

  // 새 탭으로 열기
  // window.open(whiteboardUrl, '_blank');
}

/**
 * 문서 공유 세션 시작
 * v1: 각 페이지마다 독립 collabDetails 생성 → broadcast-batch → 백엔드 파일 스토어 업로드 → 메타데이터 설정
 */

interface ImageData {
  mimeType: string;
  id: string;
  dataURL: string | null;
  created: number;
  thumbnailURL: string;
}

interface ImageElement {
  type: 'image';
  id: string;
  x: number;
  y: number;
  width: number;
  height: number;
  angle: number;
  strokeColor: string;
  backgroundColor: string;
  fillStyle: string;
  strokeWidth: number;
  strokeStyle: string;
  roughness: number;
  opacity: number;
  fileId: string;
  locked: boolean;
  link: null;
  status: string;
  scale: [number, number];
  groupIds: string[];
  roundness: null;
  seed: number;
  version: number;
  versionNonce: number;
  isDeleted: boolean;
  boundElements: null;
  updated: number;
  pageNumber: number;
}

interface DocShareFileMetadata {
  files: ImageData[];
  imageElements: ImageElement[];
}

export async function startDocumentSharingSession(
  pageCount: number,
  fileMetadata: DocShareFileMetadata,
  pdfFileName: string,
  isAllEditable = false,
): Promise<void> {
  const room = getRoom();
  if (!room) return;

  const metadataHandler = getMetadataHandler();
  if (!metadataHandler) return;

  const ownerId = room.myUserId();

  // 페이지별 collabDetails 생성 + roomList 구성
  const collabDetailList: Array<{ collabDetails: CollabDetails; pageNum: number }> = [];
  const roomList: Array<{ roomID: string; roomKey: string; rawData: ImageElement[] }> = [];

  for (let i = 0; i < pageCount; i++) {
    const collabDetails = await generateCollaborationLinkData();
    collabDetailList.push({ collabDetails, pageNum: i + 1 });

    const elements = [
      fileMetadata.imageElements.find(el => el.pageNumber === i + 1),
    ].filter(Boolean) as ImageElement[];

    roomList.push({
      roomID: collabDetails.roomId,
      roomKey: collabDetails.roomKey,
      rawData: elements,
    });
  }

  // broadcast-batch: collab 서버에 각 페이지 초기 데이터 전송
  await axios({
    headers: { 'Content-Type': 'application/json' },
    method: 'POST',
    url: `${COLLAB_SERVER_URL}/api/broadcast-batch`,
    data: {
      roomList,
      pdfFileName,
      conferenceId: room.getName?.() ?? room.options?.name ?? '',
      ownerJitsiId: ownerId,
      editPermissions: isAllEditable ? null : [ownerId],
    },
  });

  // 백엔드 파일 스토어에 각 페이지 이미지 업로드
  // ExcalidrawApp 마운트 전에 업로드해야 X박스 없이 바로 표시됨
  if (fileMetadata.files) {
    await Promise.all(
      collabDetailList.map(async ({ collabDetails, pageNum }) => {
        const fileData = fileMetadata.files[pageNum - 1];
        if (!fileData || !fileData.thumbnailURL || !fileData.id) return;
        await uploadImageToBackendFileStore(
          collabDetails.roomId,
          fileData.id,
          fileData.thumbnailURL,
          collabDetails.roomKey,
          fileData.mimeType || 'image/png',
        );
      })
    );
  }

  // 메타데이터 설정 → METADATA_UPDATED 이벤트 → 모든 참여자에게 전달
  // ⚠️ FILE_METADATA를 WHITEBOARD_ID보다 먼저 설정해야
  // METADATA_UPDATED 핸들러가 files 데이터를 읽을 수 있음
  metadataHandler.setMetadata(METADATA_KEYS.WHITEBOARD_OWNER, ownerId);
  metadataHandler.setMetadata(METADATA_KEYS.FILE_METADATA, fileMetadata);
  metadataHandler.setMetadata(METADATA_KEYS.WHITEBOARD_ID, [...collabDetailList]);

  // 1페이지 기준 collabDetails
  const firstCollab = collabDetailList[0]?.collabDetails;
  if (!firstCollab) return;

  const { local } = store.getState();
  const ownerName = local.userName || local.nickname || '나';
  store.dispatch(participantsActions.addPseudoParticipant({
    id: WHITEBOARD_ID,
    name: `${ownerName}의 문서 공유`,
    isWhiteboard: true,
    ownerId,
  }));
  store.dispatch(whiteboardActions.openWhiteboard({
    ownerId,
    isSketchMode: false,
    collabDetails: firstCollab,
    isOwner: true,
  }));
  // 화이트보드를 메인 유저로 핀 고정 + 사이드뷰로 전환
  store.dispatch(mainUserActions.pinUser(WHITEBOARD_ID));
  store.dispatch(uiActions.setScreenMode('SIDE'));
  store.dispatch(whiteboardActions.setCollabDetailList(
    collabDetailList.map(({ collabDetails, pageNum }) => ({ pageNum, collabDetails }))
  ));
  store.dispatch(whiteboardActions.setDocShareFiles(
    fileMetadata.files.map(f => ({
      mimeType: f.mimeType,
      id: f.id,
      dataURL: f.dataURL,
      created: f.created,
      thumbnailURL: f.thumbnailURL,
    }))
  ));
  store.dispatch(whiteboardActions.setFileMetadata({
    fileName: pdfFileName,
    totalPages: pageCount,
    fileId: `file-${Date.now()}`,
  }));
  store.dispatch(whiteboardActions.setTotalPages(pageCount));

  // ─── 모바일 테스트용: 새 탭으로 WhiteboardPage 열기 ────────────────────
  const whiteboardUrl = appendURLHashParam(
    window.location.origin + '/whiteboard',
    'state',
    JSON.stringify({
      roomList: [...collabDetailList],
      localParticipantName: ownerName,
      jitsiId: ownerId,
      initialZoom: 0.5,
      conferenceId: room.getName?.() ?? room.options?.name ?? '',
    }),
  );

  // 네이티브 앱이 WebView에 postMessage로 보내야 하는 FILE_LIST 데이터
  const nativeFileListMessage = JSON.stringify({
    type: 'FILE_LIST',
    files: [...fileMetadata.files],
  });

  console.log('\n========== [모바일테스트용] 문서공유 모드 ==========');
  console.log('■ WhiteboardPage URL (네이티브에서 WebView로 열어야 할 URL):');
  console.log(whiteboardUrl);
  console.log('\n■ 네이티브 → WebView postMessage (WebView 로드 후 전송):');
  console.log(nativeFileListMessage);
  console.log('\n■ 네이티브 코드 예시:');
  console.log(`  [Android] webView.evaluateJavascript("window.postMessage('" + fileListJson + "')", null)`);
  console.log(`  [iOS]     webView.evaluateJavaScript("window.postMessage('" + fileListJson + "')")`);
  console.log('==================================================\n');

  // 새 탭으로 열기
  // const newTab = window.open(whiteboardUrl, '_blank');

  // // 테스트용: 새 탭이 로드된 후 FILE_LIST 메시지 1회 전송
  // if (newTab) {
  //   setTimeout(() => {
  //     try {
  //       newTab.postMessage(nativeFileListMessage, '*');
  //       console.log('[모바일테스트용] FILE_LIST 메시지를 새 탭에 전송 완료');
  //     } catch (e) {
  //       console.error('[모바일테스트용] FILE_LIST 전송 실패:', e);
  //     }
  //   }, 3000);
  // }
}

/**
 * 화이트보드 세션 종료 (스케치 + 문서공유 공통)
 */
export function stopWhiteboardSession(): void {
  const metadataHandler = getMetadataHandler();
  console.log('[DocShare:stopWB] metadataHandler:', !!metadataHandler);
  if (!metadataHandler) return;

  // 메타데이터 제거 → METADATA_UPDATED 이벤트 발송
  // ※ undefined가 아닌 null 사용 — JSON.stringify시 undefined는 필드 자체가 제거되어
  //    서버가 메타데이터 삭제 요청을 인식하지 못함 (v1 원본도 null 사용)
  metadataHandler.setMetadata(METADATA_KEYS.WHITEBOARD_ID, null);
  metadataHandler.setMetadata(METADATA_KEYS.WHITEBOARD_OWNER, null);
  metadataHandler.setMetadata(METADATA_KEYS.FILE_METADATA, { files: null });
  console.log('[DocShare:stopWB] metadata cleared');

  // mainUser가 화이트보드였으면 초기화 (pinnedUserId + mainUserId 모두 해제)
  const { mainUser } = store.getState();
  if (mainUser.pinnedUserId === WHITEBOARD_ID) {
    store.dispatch(mainUserActions.pinUser(null));
  }
  if (mainUser.mainUserId === WHITEBOARD_ID) {
    store.dispatch(mainUserActions.setMainUser('localUser'));
  }

  store.dispatch(whiteboardActions.reset());
  store.dispatch(participantsActions.removePseudoParticipant(WHITEBOARD_ID));
}

// ─── 파일 업로드/변환 ──────────────────────────────────────────────────────

/**
 * 파일 업로드 → Object Storage → Office Preview → 문서공유 시작
 * v1: handleFileUploadForDocumentShare()
 *
 * 플로우:
 * 1) uploadFiles — WEHAGO Object Storage에 파일 업로드 (S3)
 * 2) previewFilesUnauthorized — PDF/Office → 페이지별 이미지 URL 변환
 * 3) 이미지 로드 → imageElement + imageData 구성
 * 4) startDocumentSharingSession — per-page collab room 생성
 */
export async function handleFileUploadForDocumentShare(file: File): Promise<void> {
  const room = getRoom();
  if (!room) return;

  // 1) Object Storage에 파일 업로드
  const uploadResult = await uploadFiles(file);
  if (!uploadResult) {
    console.error('[Whiteboard] 파일 업로드 실패');
    return;
  }

  // 2) Office Preview — 페이지별 이미지 URL 변환
  const ext = file.name.lastIndexOf('.') !== -1
    ? file.name.substring(file.name.lastIndexOf('.') + 1)
    : '';

  const previewResult = await previewFilesUnauthorized({
    FileName: uploadResult.newFileName,
    BucketType: uploadResult.bucketType,
    cno: undefined,
    Ext: ext,
    contentType: file.type,
  });

  if (!previewResult?.resultList || previewResult.resultList.length === 0) {
    console.error('[Whiteboard] 문서 미리보기 변환 실패');
    return;
  }

  const firstResult = previewResult.resultList[0];
  // getOfficePreView는 { resources: string[] } 형태, getImageURL은 string 자체를 반환할 수 있음
  const resources: string[] = typeof firstResult === 'string'
    ? previewResult.resultList as unknown as string[]
    : (firstResult as { resources?: string[] }).resources || [];

  if (!resources || resources.length === 0) {
    console.error('[Whiteboard] 문서 리소스가 없습니다.', previewResult);
    return;
  }

  const imageDataList: ImageData[] = [];
  const imageElements: ImageElement[] = [];

  // 3) 각 페이지 이미지 로드 → 크기 측정 → imageElement/imageData 구성
  for (let i = 0; i < resources.length; i++) {
    const fileId = `pdf-page-${i + 1}-${Date.now()}`;
    // 개발환경 URL 치환 (thumbnailURL에도 치환된 URL 저장)
    const imgSrc = resources[i].replace(
      'http://dev.api0.wehago.com/',
      'https://rtctest.wehago.com/api0/',
    );

    const imageData: ImageData = {
      mimeType: 'image/png',
      id: fileId,
      dataURL: null,
      created: Date.now(),
      thumbnailURL: imgSrc,
    };

    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.src = imgSrc;
    await new Promise<void>((resolve) => {
      img.onload = () => resolve();
      img.onerror = () => resolve(); // 에러시에도 진행
    });

    const imageElement: ImageElement = {
      type: 'image',
      id: fileId,
      x: 0,
      y: 0,
      width: img.width || 800,
      height: img.height || 600,
      angle: 0,
      strokeColor: 'transparent',
      backgroundColor: 'transparent',
      fillStyle: 'solid',
      strokeWidth: 1,
      strokeStyle: 'solid',
      roughness: 0,
      opacity: 100,
      fileId,
      locked: true,
      link: null,
      status: 'saved',
      scale: [1, 1],
      groupIds: [],
      roundness: null,
      seed: Math.floor(Math.random() * 1000000),
      version: 1,
      versionNonce: Math.floor(Math.random() * 1000000),
      isDeleted: false,
      boundElements: null,
      updated: Date.now(),
      pageNumber: i + 1,
    };

    imageElements.push(imageElement);
    imageDataList.push(imageData);
  }

  // 4) 문서공유 세션 시작
  await startDocumentSharingSession(
    imageElements.length,
    { files: [...imageDataList], imageElements: [...imageElements] },
    file.name,
    false,
  );
}

/**
 * 회의자료(MaterialFileInfo)로 문서공유 시작
 * handleFileUploadForDocumentShare와 동일 흐름이지만,
 * 이미 Object Storage에 업로드된 파일의 메타데이터를 활용하여 변환만 수행.
 */
export async function handleMaterialDocumentShare(material: {
  fileName: string;
  newFileName: string;
  bucketType: string;
}): Promise<void> {
  const room = getRoom();
  if (!room) return;

  store.dispatch(whiteboardActions.setIsFileLoading(true));

  try {
    const ext = material.fileName.lastIndexOf('.') !== -1
      ? material.fileName.substring(material.fileName.lastIndexOf('.') + 1).toLowerCase()
      : '';

    let inferredContentType = '';
    if (['png', 'jpg', 'jpeg', 'gif', 'webp'].includes(ext)) {
      inferredContentType = `image/${ext === 'jpg' ? 'jpeg' : ext}`;
    }

    // Office Preview — 페이지별 이미지 URL 변환
    const previewResult = await previewFilesUnauthorized({
      FileName: material.newFileName,
      BucketType: material.bucketType,
      cno: undefined,
      Ext: ext,
      contentType: inferredContentType,
    });

    if (!previewResult?.resultList || previewResult.resultList.length === 0) {
      console.error('[Whiteboard] 회의자료 문서 미리보기 변환 실패');
      return;
    }

    const firstResult = previewResult.resultList[0];
    const resources: string[] = typeof firstResult === 'string'
      ? previewResult.resultList as unknown as string[]
      : (firstResult as { resources?: string[] }).resources || [];

    if (!resources || resources.length === 0) {
      console.error('[Whiteboard] 회의자료 문서 리소스가 없습니다.', previewResult);
      return;
    }

    const imageDataList: ImageData[] = [];
    const imageElements: ImageElement[] = [];

    for (let i = 0; i < resources.length; i++) {
      const fileId = `pdf-page-${i + 1}-${Date.now()}`;
      const imgSrc = resources[i].replace(
        'http://dev.api0.wehago.com/',
        'https://rtctest.wehago.com/api0/',
      );

      const imageData: ImageData = {
        mimeType: 'image/png',
        id: fileId,
        dataURL: null,
        created: Date.now(),
        thumbnailURL: imgSrc,
      };

      const img = new Image();
      img.crossOrigin = 'anonymous';
      img.src = imgSrc;
      await new Promise<void>((resolve) => {
        img.onload = () => resolve();
        img.onerror = () => resolve();
      });

      const imageElement: ImageElement = {
        type: 'image',
        id: fileId,
        x: 0,
        y: 0,
        width: img.width || 800,
        height: img.height || 600,
        angle: 0,
        strokeColor: 'transparent',
        backgroundColor: 'transparent',
        fillStyle: 'solid',
        strokeWidth: 1,
        strokeStyle: 'solid',
        roughness: 0,
        opacity: 100,
        fileId,
        locked: true,
        link: null,
        status: 'saved',
        scale: [1, 1],
        groupIds: [],
        roundness: null,
        seed: Math.floor(Math.random() * 1000000),
        version: 1,
        versionNonce: Math.floor(Math.random() * 1000000),
        isDeleted: false,
        boundElements: null,
        updated: Date.now(),
        pageNumber: i + 1,
      };

      imageElements.push(imageElement);
      imageDataList.push(imageData);
    }

    await startDocumentSharingSession(
      imageElements.length,
      { files: [...imageDataList], imageElements: [...imageElements] },
      material.fileName,
      false,
    );
  } finally {
    store.dispatch(whiteboardActions.setIsFileLoading(false));
  }
}

// ─── 쿼리 함수 ────────────────────────────────────────────────────────────

export function getIsOpenWhiteboardSession(): boolean {
  const { participants } = store.getState();
  return participants.participantIds.includes(WHITEBOARD_ID);
}

export function getIsWhiteboardOwner(): boolean {
  const { whiteboard } = store.getState();
  return whiteboard.isOwner;
}

export function getIsDocumentShare(): boolean {
  const { whiteboard } = store.getState();
  return whiteboard.isOpen && !whiteboard.isSketchMode;
}

export function getIsSketchMode(): boolean {
  const { whiteboard } = store.getState();
  return whiteboard.isOpen && whiteboard.isSketchMode;
}

// ─── 백엔드 파일 스토어 ────────────────────────────────────────────────────

/**
 * 이미지 URL에서 바이너리를 가져와 백엔드 파일 스토어에 업로드
 * ExcalidrawApp이 마운트된 후 storageBackendUrl을 통해 즉시 올바른 이미지를
 * 서빙받을 수 있도록 metadata 설정 전에 호출해야 한다.
 * v1: uploadImageToBackendFileStore()
 */
export async function uploadImageToBackendFileStore(
  roomId: string,
  fileId: string,
  thumbnailURL: string,
  roomKey: string,
  mimeType = 'image/png',
): Promise<void> {
  try {
    const url = thumbnailURL.replace(
      'http://dev.api0.wehago.com/',
      'https://rtctest.wehago.com/api0/',
    );
    const dataURL = await convertUrlToBase64(url);
    if (!dataURL) {
      console.warn('[Whiteboard] 이미지 dataURL 변환 실패 (업로드 스킵):', url);
      return;
    }

    await fetch(`${COLLAB_SERVER_URL}/api/v1/documents/sessions/${roomId}/files`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        fileId,
        dataURL,
        roomKey,
        mimeType,
        created: Date.now(),
      }),
    });
  } catch (err) {
    console.error('[Whiteboard] 백엔드 파일 스토어 업로드 실패:', err);
  }
}

// ─── 방 정보 조회 ────────────────────────────────────────────────────────

/**
 * Collab 서버에서 방 참여자/권한 정보 조회
 * v1: getRoomInfo()
 */
export async function getRoomInfo(
  conferenceId: string,
): Promise<{ editPermissions?: unknown; pdfFileName?: string; participants?: Array<{ isOwner?: boolean; jitsiId?: string }> } | null> {
  try {
    const res = await fetch(`${COLLAB_SERVER_URL}/api/participants-permissions?conferenceId=${encodeURIComponent(conferenceId)}`);
    if (!res.ok) return null;
    return await res.json();
  } catch {
    return null;
  }
}
